import { useState } from "react";
import { FONT_BODY, FONT_DISPLAY } from "./theme";
import { hasHouseRules } from "./house-rules";
import { HouseRulesSummary, HOUSE_RULES_GUARDRAIL } from "./house-rules-card";

// ============================================================
// BLACK OWL - Rules & How to Play
// Reusable panel accessible from Home and Game screens
// ============================================================

// --- Section component ---
function RulesSection({ title, icon, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{
      marginBottom: "10px",
      background: open ? "rgba(201, 162, 39, 0.04)" : "rgba(255,255,255,0.02)",
      border: `1px solid ${open ? "rgba(201, 162, 39, 0.15)" : "rgba(255,255,255,0.05)"}`,
      borderRadius: "10px",
      overflow: "hidden",
      transition: "all 0.25s ease",
    }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%", padding: "14px 18px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "none", border: "none", cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "18px" }}>{icon}</span>
          <span style={{
            fontSize: "13px", fontWeight: 700, letterSpacing: "1.5px",
            textTransform: "uppercase", color: "#c9a227",
            fontFamily: FONT_BODY,
          }}>{title}</span>
        </div>
        <span style={{
          fontSize: "16px", color: "#5a5a6e",
          transform: open ? "rotate(180deg)" : "rotate(0)",
          transition: "transform 0.25s ease",
        }}>▾</span>
      </button>
      {open && (
        <div style={{ padding: "0 18px 16px" }}>
          <div style={{
            height: "1px", background: "rgba(201, 162, 39, 0.1)",
            marginBottom: "14px",
          }}/>
          {children}
        </div>
      )}
    </div>
  );
}

// --- Points table row ---
function PointRow({ label, value, color = "#c9a227" }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "8px 12px",
      background: "rgba(255,255,255,0.02)",
      borderRadius: "6px", marginBottom: "4px",
    }}>
      <span style={{
        fontSize: "12px", color: "#a0a0b0",
        fontFamily: FONT_BODY,
      }}>{label}</span>
      <span style={{
        fontSize: "13px", fontWeight: 700, color: color,
        fontFamily: FONT_BODY,
      }}>{value}</span>
    </div>
  );
}

// --- Paragraph text ---
function RuleText({ children }) {
  return (
    <p style={{
      fontSize: "13px", color: "#a0a0b0", margin: "0 0 10px",
      fontFamily: FONT_BODY, lineHeight: 1.6,
    }}>{children}</p>
  );
}

// --- Step item ---
function StepItem({ number, title, description }) {
  return (
    <div style={{
      display: "flex", gap: "12px", marginBottom: "14px",
    }}>
      <div style={{
        width: "28px", height: "28px", borderRadius: "50%",
        background: "rgba(201, 162, 39, 0.15)",
        border: "1px solid rgba(201, 162, 39, 0.3)",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <span style={{
          fontSize: "12px", fontWeight: 700, color: "#c9a227",
          fontFamily: FONT_BODY,
        }}>{number}</span>
      </div>
      <div>
        <p style={{
          fontSize: "13px", fontWeight: 700, color: "#e8e6df",
          margin: "0 0 2px", fontFamily: FONT_BODY,
        }}>{title}</p>
        <p style={{
          fontSize: "12px", color: "#7a7a8e", margin: 0,
          fontFamily: FONT_BODY, lineHeight: 1.5,
        }}>{description}</p>
      </div>
    </div>
  );
}

// --- Sub-header ---
function SubHeader({ children }) {
  return (
    <p style={{
      fontSize: "11px", fontWeight: 700, letterSpacing: "2px",
      textTransform: "uppercase", color: "#c9a227",
      margin: "14px 0 8px", fontFamily: FONT_BODY,
    }}>{children}</p>
  );
}

// --- Main Rules Panel ---
export default function RulesPanel({ onClose, houseRules }) {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "absolute", inset: 0,
          background: "rgba(0, 0, 0, 0.8)",
          backdropFilter: "blur(8px)",
        }}
      />

      {/* Panel */}
      <div style={{
        position: "relative", zIndex: 1,
        width: "100%", maxWidth: "460px", maxHeight: "85vh",
        margin: "20px",
        background: "rgba(12, 12, 22, 0.95)",
        border: "1px solid rgba(201, 162, 39, 0.2)",
        borderRadius: "16px",
        boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
        display: "flex", flexDirection: "column",
        overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{
          padding: "20px 24px",
          borderBottom: "1px solid rgba(201, 162, 39, 0.1)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <div>
            <h2 style={{
              fontSize: "18px", fontWeight: 700, color: "#c9a227",
              margin: 0, letterSpacing: "3px", textTransform: "uppercase",
              fontFamily: FONT_DISPLAY,
            }}>How to Play</h2>
            <p style={{
              fontSize: "11px", color: "#5a5a6e", margin: "4px 0 0",
              fontFamily: FONT_BODY,
            }}>Black Owl — Fantasy Draft Game</p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: "32px", height: "32px",
              fontSize: "18px", color: "#5a5a6e",
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "8px", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >✕</button>
        </div>

        {/* Scrollable content */}
        <div style={{
          padding: "16px 20px",
          overflowY: "auto", flex: 1,
        }}>
          {/* Overview */}
          {/* FB-27: this lobby's House Rules, read-only (only inside a game) */}
          {houseRules && (
            <RulesSection title="House Rules" icon="📜" defaultOpen={hasHouseRules(houseRules)}>
              <HouseRulesSummary rules={houseRules} />
              <RuleText>{HOUSE_RULES_GUARDRAIL}</RuleText>
            </RulesSection>
          )}

          <RulesSection title="What is Black Owl?" icon="🦉" defaultOpen={true}>
            <RuleText>
              Black Owl is a social NFL game designed to be played with friends in the same room while watching live football. Draft real NFL players, earn arrows when your players make plays, and fire those arrows at your friends for real-life penalties.
            </RuleText>
            <RuleText>
              The game combines fantasy football drafting with real-time scoring and a social party game element that keeps everyone engaged throughout the entire broadcast.
            </RuleText>
          </RulesSection>

          {/* How to Play */}
          <RulesSection title="How to Play" icon="📋">
            <StepItem
              number="1"
              title="Create or Join a Lobby"
              description="The host creates a lobby, selects NFL games to draft from, and shares the lobby code. Other players join using the code."
            />
            <StepItem
              number="2"
              title="Draft Your Team"
              description="Take turns drafting real NFL players from the selected games. Build your roster with a QB, RB, WR, and TE. Choose wisely — your arrows depend on how your players perform."
            />
            <StepItem
              number="3"
              title="Watch & Earn"
              description="Everyone starts the game with one arrow for each other player in the lobby. As the NFL games play out in real time, your drafted players earn more arrows based on their performance — touchdowns, yardage milestones, and more."
            />
            <StepItem
              number="4"
              title="Fire Arrows"
              description="Fire your earned arrows at other players in the lobby. Each arrow that lands gives them a penalty they must pay off with a real-life action decided by the group."
            />
            <StepItem
              number="5"
              title="Pay Your Penalties"
              description="Got hit with penalties? The group decides what the action is — push-ups, a dare, making snacks — and you mark them as paid in the app once completed."
            />
          </RulesSection>

          {/* Scoring */}
          <RulesSection title="Scoring Plays" icon="🏈">
            <RuleText>
              When your drafted player is involved in a scoring play, you earn arrows instantly.
            </RuleText>
            <SubHeader>Touchdowns & Field Goals</SubHeader>
            <PointRow label="Rushing Touchdown" value="+6" />
            <PointRow label="Receiving Touchdown" value="+6" />
            <PointRow label="Passing Touchdown (QB)" value="+6" />
            <PointRow label="Field Goal" value="+3" />
          </RulesSection>

          {/* Yardage */}
          <RulesSection title="Yardage Milestones" icon="📏">
            <RuleText>
              Arrows are also earned when your players hit yardage milestones during the game. These accumulate as the game progresses.
            </RuleText>

            <SubHeader>Passing Yards</SubHeader>
            <PointRow label="50 yards" value="+1" />
            <PointRow label="100 yards" value="+2" />
            <PointRow label="200 yards" value="+2" />
            <PointRow label="300 yards" value="+2" />
            <PointRow label="400 yards" value="+5" />

            <SubHeader>Rushing Yards</SubHeader>
            <PointRow label="Every 10 yards (10-90)" value="+1 each" />
            <PointRow label="100 yards" value="+5" />

            <SubHeader>Receiving Yards</SubHeader>
            <PointRow label="Every 10 yards (10-90)" value="+1 each" />
            <PointRow label="100 yards" value="+5" />

            <RuleText>
              Yardage follows the real box score. A run or catch for negative yards lowers your player's total, but arrows already earned are never taken back, and each milestone pays out only once. Sacks don't reduce passing yards. A run or catch that ends in a fumble keeps the yards gained before the fumble, and a play wiped out by a penalty doesn't count.
            </RuleText>
          </RulesSection>

          {/* Negative Events */}
          <RulesSection title="Negative Events" icon="⚠">
            <RuleText>
              Your players can also cost you arrows. Turnovers and mistakes result in arrow deductions.
            </RuleText>
            <PointRow label="Interception thrown" value="-2" color="#e85555" />
            <PointRow label="Fumble lost (opponent recovers)" value="-2" color="#e85555" />
            <PointRow label="Missed or blocked field goal" value="-3" color="#e85555" />
            <RuleText>
              Note: Fumbles recovered by your player's own team are not penalized. Only turnovers that give the ball to the other team count against you.
            </RuleText>
            <RuleText>
              Your arrow count never drops below zero. If a deduction is bigger than what you have, the rest becomes penalties you owe to "The Game", paid off like any other penalty.
            </RuleText>
          </RulesSection>

          {/* Spending & Penalties */}
          <RulesSection title="Firing & Penalties" icon="💰">
            <RuleText>
              The real fun begins when you fire arrows at other players. Tap Fire next to a player on the standings board to take aim — each arrow that lands becomes a penalty they owe.
            </RuleText>
            <RuleText>
              The host can set House Rules in the lobby before the draft: what one arrow means in real life (a push-up, say), plus two optional bigger shots. A Cannonball is worth a set number of arrows (10 arrows = run a lap) and a Meteor is worth more. Firing one costs exactly its worth in arrows. Without House Rules, the group just agrees what an arrow means. Some ideas: push-ups, a lap around the room, grabbing snacks for the room, or any creative dare the group agrees on.
            </RuleText>
            <RuleText>
              {HOUSE_RULES_GUARDRAIL} Anyone can decline a penalty, and no one should be pressured into one.
            </RuleText>
            <RuleText>
              When you get hit, a pop-up tells you who fired and what you owe. Your "Arrows you owe" card lists each sender. Once you've done the real-life penalty, tap "Pay 1" or "Pay all" to clear loose arrows. A Cannonball or Meteor has its own row: tap "Done" once you've served the whole thing. Everyone can see each other's unpaid penalties on the leaderboard.
            </RuleText>
          </RulesSection>

          {/* Tips */}
          <RulesSection title="Strategy Tips" icon="🧠">
            <RuleText>
              Draft players from games you're actually watching — it's more fun when you see your player score in real time.
            </RuleText>
            <RuleText>
              Don't fire all your arrows early. Save some for crucial moments late in the game when penalties hit harder.
            </RuleText>
            <RuleText>
              QBs rack up arrows steadily through passing milestones, but a breakout RB or WR who hits 100 yards earns a big +5 bonus on top of the per-10-yard arrows. Any player can earn any type of yardage — a QB who scrambles earns rushing yards, an RB who catches a pass earns receiving yards.
            </RuleText>
          </RulesSection>
        </div>
      </div>

      <style>{`
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(201, 162, 39, 0.2); border-radius: 4px; }
      `}</style>
    </div>
  );
}

// --- Trigger button (use this on Home and Game screens) ---
export function RulesButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "10px 18px",
        fontSize: "11px", fontWeight: 600, letterSpacing: "1.5px",
        textTransform: "uppercase",
        fontFamily: FONT_BODY,
        color: "#c9a227",
        background: "rgba(201, 162, 39, 0.06)",
        border: "1px solid rgba(201, 162, 39, 0.15)",
        borderRadius: "8px", cursor: "pointer",
        transition: "all 0.2s ease",
        display: "inline-flex", alignItems: "center", gap: "8px",
      }}
    >
      <span style={{ fontSize: "14px" }}>📖</span> How to Play
    </button>
  );
}
